<script setup>
// Astra DS · AstraMark — la A de Astra.
//
// Figma: `AstraMark · plano 1540:22496`. Geometría **oblicua** desde el
// 08.09.26, exportada ese día: la A abierta, con la muesca en el vértice y el
// travesaño que arranca del asta izquierda. Pegada tal cual, no se redibuja.
//
// ⛔ **Aquí hubo dos marcas antes.** Un aro con el punto rojo hasta el 02.09, y
// una A recta dentro de un cuadro hasta el 08.09. La oblicua las sustituye a las
// dos y actualizó sus 53 instancias de Figma sola.
//
// ── Dónde va cada variante, desde el 08.09.26 ──
//
// **Ninguna cabecera lleva ya fondo de color detrás de la marca.** En la de
// escritorio va la palabra entera —ver `AstraWord.vue`— y en la de móvil esta A
// con `fondo="ninguno"`, porque a 390 la palabra se comería un tercio del ancho.
//
// `rojo` y `negro` quedan para lo que siempre fue suyo: **cuando la marca viaja
// como archivo** y no hay caja que la contenga —icono de aplicación, avatar
// dentro de la interfaz de otro— y para `AboutView`, donde va suelta a 464.
//
// ⚠️ **Suelo de uso: 18 px de alto.** Por debajo la muesca del vértice se cierra
// y la A pasa a leerse como un triángulo macizo.
//
// ⚠️ **La A no es cuadrada**: mide 1,379 de ancho por 1 de alto. El `viewBox` de
// 419 es el lienzo del componente, con su aire; quien la meta en una caja
// cuadrada y la estire «a lo grande» la deforma. Por eso `width`/`height` van
// los dos a `size`: el dibujo se centra dentro y no se toca.
import { useId } from "vue";

defineProps({
  size: { type: [Number, String], default: 26 },
  // `ninguno` — la A sola, **con el degradado del logotipo**, para cuando quien
  // controla la caja pone el fondo o no hay fondo ninguno.
  //
  // `rojo` — el cuadro lleno con la A recortada en tinta. La marca completa en
  // un archivo, para cuando viaja sola.
  fondo: {
    type: String,
    default: "rojo",
    validator: (v) => ["rojo", "ninguno"].includes(v),
  },
});

// Ver `AstraWord.vue`: un id por instancia, o la segunda marca de la página se
// queda sin degradado en cuanto la primera se desmonta.
const id = `astra-mark-grad-${useId()}`;
</script>

<template>
  <svg
    class="astra-mark"
    viewBox="0 0 419 419"
    :width="size"
    :height="size"
    role="img"
    aria-label="Astra FM"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs v-if="fondo === 'ninguno'">
      <!-- Coordenadas del propio export, no recalculadas: la inclinación es la
           que tenía el campo Rescoldo. -->
      <linearGradient
        :id="id"
        x1="106.5"
        y1="134.188"
        x2="250.033"
        y2="330.488"
        gradientUnits="userSpaceOnUse"
      >
        <stop stop-color="var(--brasa-1)" />
        <stop offset="0.52" stop-color="var(--brasa-2)" />
        <stop offset="1" stop-color="var(--brasa-3)" />
      </linearGradient>
    </defs>

    <rect
      v-if="fondo === 'rojo'"
      class="astra-mark__fondo"
      width="419"
      height="419"
    />
    <path
      class="astra-mark__a"
      :class="{ 'astra-mark__a--tinta': fondo === 'rojo' }"
      :fill="fondo === 'rojo' ? undefined : `url(#${id})`"
      d="M46.7957 330C44.4671 330 44.079 328.448 45.6314 325.343L187.677 94.8213C190.006 90.9404 192.723 89 195.828 89H235.414C239.295 89 242.012 90.9404 243.564 94.8213L372.803 324.179C375.132 328.06 374.743 330 371.639 330H316.916C313.035 330 310.318 328.448 308.766 325.343L214.457 157.691L169 237L254.043 255.488C257.148 256.264 259.477 258.204 261.029 261.309L272.672 295.072C274.225 299.729 272.672 301.67 268.015 300.894L150 270.5L111.997 324.179C109.669 328.06 106.952 330 103.847 330H46.7957Z"
    />
  </svg>
</template>

<style scoped lang="scss">
// ── El degradado, aquí y en hex ──
//
// 🔴 **No son tokens, y es una decisión, no un descuido.** Pablo cerró el
// 08.09.26 que las tres paradas viven en el componente: el logotipo lleva color
// propio —ya no cuelga de `icon/accent`— y un token de sistema para un
// degradado que solo usa él confunde más de lo que ordena. Está escrito en
// DESIGN.md § El degradado se queda en el código.
//
// ⚠️ **Voltean con el modo.** En Figma las paradas de un degradado no admiten
// variable, así que el archivo solo puede llevar unas —las de oscuro— y las de
// claro se resuelven aquí. Sobre papel el degradado tiene que empezar por el
// extremo oscuro o se lava.
//
// ⚠️ `#b72a25` sobre el lienzo oscuro da **3,08** de contraste, y el mínimo
// para un elemento gráfico es 3,00: está dentro, pero sin margen.
// **No oscurecer más ese extremo** — `#a02620` cae a 2,55.
.astra-mark {
  --brasa-1: #cc5238;
  --brasa-2: #c2402c;
  --brasa-3: #b72a25;

  display: block;
  flex: none;
  // Ver AstraIcon: la capa legacy estira todo `svg` al 100% y un selector de
  // etiqueta gana al atributo. Sin esto la marca ocupa 53×26.
  width: auto;
  height: auto;
}

.astra-mark__fondo {
  fill: var(--color-brand-mark-red);
}

// 🔴 **El color va por clase, no en `.astra-mark__a` a secas, y es por orden de
// cascada.** En SVG una regla CSS gana al atributo `fill` del elemento, así que
// pintar aquí la tinta tapaba el degradado de la variante `ninguno` — el
// atributo no llegaba a aplicarse nunca. Con la clase condicional, en `ninguno`
// no hay regla que pisar y manda el atributo.
.astra-mark__a--tinta {
  fill: var(--color-brand-mark-ink);
}

// El juego de claro. Sin `:global()`: Vue aplica el scope al ÚLTIMO selector,
// así que esto compila a `html[data-theme=light] .astra-mark[data-v-…]` y casa. Con
// `:global()` no llegaba a generarse la regla — comprobado en el navegador el
// 09.09.26, el degradado se quedaba en las paradas de oscuro sobre papel.
html[data-theme="light"] .astra-mark {
  --brasa-1: #b72a25;
  --brasa-2: #c14434;
  --brasa-3: #c95a44;
}
</style>
